import React, { useState } from 'react';
import { Container, Typography, Box, Button, Grid, styled, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Fab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { fetchCategoriesWithDrinks } from '../../services/categoryApi';
import { recordMenuClick } from '../../services/menuClickApi';
import { fetchPublicFeatureFlags } from '../../services/featureFlagsApi';
import { FONT_FAMILY } from '../../theme';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import LoungePolicyBanner from '../../components/LoungePolicyBanner/LoungePolicyBanner';
import PromotionBanner from '../../components/PromotionBanner/PromotionBanner';
import PromotionalBanner from '../../components/PromotionalBanner/PromotionalBanner';
import FeaturedItemsModal from '../../components/FeaturedItemsModal/FeaturedItemsModal';
import { isFeaturedPromotionActive } from '../../utils/featuredDrinkUtils';
import './Menu.css';

const CategoryButton = styled(Button)(({ theme, active }) => ({
  color: active ? '#ff2d2d' : '#222',
  fontSize: '1.05rem',
  padding: '8px 18px',
  minWidth: 'auto',
  width: 'auto',
  height: '100%',
  fontWeight: active ? 700 : 500,
  backgroundColor: 'transparent',
  textTransform: 'none',
  borderRadius: 0,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  transition: 'color 0.2s cubic-bezier(.4,2,.3,1)',
  boxShadow: 'none',
  '&:hover': {
    color: '#ff6b00',
    backgroundColor: 'transparent',
    opacity: 1,
  },
  '&::after': {
    content: '""',
    display: active ? 'block' : 'none',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -2,
    height: '2px',
    background: '#ff2d2d',
    borderRadius: 2,
    transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
  },
}));

const MenuItem = styled(Box)(({ theme }) => ({
  padding: '6px 0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

function extractMenuHighlights(categoriesData, language) {
  const parsePrice = (value) => {
    if (value == null) return null;
    const n = parseFloat(String(value).trim().replace(/[,\s]+/g, '.'));
    return Number.isFinite(n) ? n : null;
  };

  const promoItems = [];
  const featuredItems = [];

  categoriesData.forEach((category) => {
    if (!category.drinks || !Array.isArray(category.drinks)) {
      return;
    }

    category.drinks.forEach((drink) => {
      if (drink.is_promotional && drink.promotional_price && drink.price) {
        const price = Number(drink.price);
        const promoPrice = Number(drink.promotional_price);
        const discount = Math.round(((price - promoPrice) / price) * 100);
        promoItems.push({
          name: language === 'bg' && drink.name_bg ? drink.name_bg : drink.name,
          oldPrice: price.toFixed(2),
          newPrice: promoPrice.toFixed(2),
          discount: `${discount}%`,
          category_id: category.id,
          drink_id: drink.id,
        });
      }

      if (isFeaturedPromotionActive(drink)) {
        const price = parsePrice(drink.price);
        featuredItems.push({
          drink_id: drink.id,
          category_id: category.id,
          name: language === 'bg' && drink.name_bg ? drink.name_bg : drink.name,
          description: language === 'bg' && drink.featured_description_bg
            ? drink.featured_description_bg
            : (drink.featured_description || ''),
          image: drink.featured_image || drink.image || '',
          featuredUntil: drink.featured_until || null,
          priceLabel: price != null ? `${price.toFixed(2)} €` : '',
          amount: drink.amount,
          unit: drink.unit,
        });
      }
    });
  });

  return { promoItems, featuredItems };
}

const Menu = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(null);
  // Initialize language from i18n or localStorage
  const [language, setLanguage] = useState(() => {
    return i18n.language || localStorage.getItem('menuLanguage') || 'en';
  });
  // default to 'hot' when API isn't available; will be overridden when API loads
  const [expandedCategories, setExpandedCategories] = React.useState(['hot']); // ids (string/number)
  const [apiCategories, setApiCategories] = React.useState(null);
  const [loadingCategories, setLoadingCategories] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [showPromotionalBanner, setShowPromotionalBanner] = React.useState(false);
  const [showFeaturedShowcase, setShowFeaturedShowcase] = React.useState(false);
  const [promotionalDrinks, setPromotionalDrinks] = React.useState([]);
  const [featuredDrinks, setFeaturedDrinks] = React.useState([]);
  const [highlightedDrinkId, setHighlightedDrinkId] = React.useState(null);
  const highlightTimeoutRef = React.useRef(null);

  // Sync language state with i18n.language when it changes (one-way: i18n -> state)
  React.useEffect(() => {
    if (i18n.language && i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  // Fetch menu feature flags
  React.useEffect(() => {
    const loadFeatureFlag = async () => {
      try {
        const flags = await fetchPublicFeatureFlags();
        const bannerFlag = flags.find(f => f.key === 'enable_promotional_banner');
        const featuredFlag = flags.find(f => f.key === 'enable_featured_items');
        setShowPromotionalBanner(bannerFlag?.value === true || bannerFlag?.enabled === true);
        setShowFeaturedShowcase(featuredFlag?.value === true || featuredFlag?.enabled === true);
      } catch (error) {
        setShowPromotionalBanner(false);
        setShowFeaturedShowcase(false);
      }
    };
    loadFeatureFlag();
  }, []);

  // When user changes language via toggle, update localStorage and i18n
  const handleLanguageChange = React.useCallback((newLang) => {
    setLanguage(newLang);
    localStorage.setItem('menuLanguage', newLang);
    i18n.changeLanguage(newLang);
  }, [i18n]);

  const [tappedDrinkId, setTappedDrinkId] = React.useState(null);
  const tapTimerRef = React.useRef(null);

  const markDrinkTapped = (drinkId) => {
    recordMenuClick('drink', drinkId);
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }
    setTappedDrinkId(null);
    requestAnimationFrame(() => {
      setTappedDrinkId(drinkId);
      tapTimerRef.current = setTimeout(() => setTappedDrinkId(null), 900);
    });
  };

  // Move categories inside the component so it updates on language change
  // Load categories with drinks from public API; fall back to static list while loading
  const categories = React.useMemo(() => {
    if (apiCategories && Array.isArray(apiCategories)) {
      // Filter to only show active categories
      return apiCategories
        .filter(c => c.is_active !== false)
        .map(c => ({
          id: c.id,
          label: language === 'bg' && c.name_bg ? c.name_bg : c.name
        }));
    }
    // fallback static labels (translations)
    return [
      { id: 'hot', label: t('menu.categories.hot') },
      { id: 'cold', label: t('menu.categories.cold') },
      { id: 'teas', label: t('menu.categories.teas') },
      { id: 'lemonades', label: t('menu.categories.lemonades') },
      { id: 'alcoholic', label: t('menu.categories.alcoholic') },
      { id: 'ciders', label: t('menu.categories.ciders') },
      { id: 'beer', label: t('menu.categories.beer') },
      { id: 'rum', label: t('menu.categories.rum') },
      { id: 'energy', label: t('menu.categories.energy') },
      { id: 'vodka', label: t('menu.categories.vodka') },
      { id: 'whiskey', label: t('menu.categories.whiskey') },
      { id: 'cognac', label: t('menu.categories.cognac') },
      { id: 'gin', label: t('menu.categories.gin') },
      { id: 'tequila', label: t('menu.categories.tequila') },
      { id: 'wine', label: t('menu.categories.wine') },
      { id: 'shots', label: t('menu.categories.shots') },
      { id: 'aperitifs', label: t('menu.categories.aperitifs') },
      { id: 'liqueur', label: t('menu.categories.liqueur') },
      { id: 'non-alcoholic', label: t('menu.categories.non-alcoholic') },
      { id: 'nuts', label: t('menu.categories.nuts') },
      { id: 'shisha', label: t('menu.categories.shisha') },
      { id: 'desserts', label: t('menu.categories.desserts') },
    ];
  }, [apiCategories, t, language]);

  // current drinks for active category (from API only)
  const currentDrinks = React.useMemo(() => {
    if (apiCategories && activeCategory != null) {
      const found = apiCategories.find(c => String(c.id) === String(activeCategory));
      return (found && Array.isArray(found.drinks)) ? found.drinks : [];
    }
    return [];
  }, [apiCategories, activeCategory]);

  const getDrinksForCategory = (categoryId) => {
    let drinks = [];
    if (apiCategories) {
      const found = apiCategories.find(c => String(c.id) === String(categoryId));
      drinks = (found && Array.isArray(found.drinks)) ? found.drinks : [];
    }
    // Filter to only show available drinks
    return drinks.filter(drink => drink.is_available !== false);
  };

  const parseNumber = (v) => {
    if (v == null) return null;
    const s = String(v).trim().replace(/[,\s]+/g, '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };

  const priceEurFor = (item) => {
    const eurPrice = item.price ?? item.price_string ?? '';
    const n = parseNumber(eurPrice);
    return n != null ? `${n.toFixed(2)} €` : '';
  };

  // Utility: split array into rows of N items
  const chunkArray = (arr = [], size = 3) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  // Responsive columns: match MUI Grid breakpoints (3 columns on md+, 1 on mobile)
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md')); // 900+
  const columns = React.useMemo(() => (isMd ? 3 : 1), [isMd]);

  // For expanded categories we compute sections of rows; flattenedRows is used for measurement
  const expandedSections = React.useMemo(() => {
    if (!Array.isArray(expandedCategories) || expandedCategories.length === 0) return [];
    return expandedCategories.map((catId) => ({ id: catId, rows: chunkArray(getDrinksForCategory(catId), columns) }));
  }, [expandedCategories, apiCategories, activeCategory, columns]);

  const flattenedRows = React.useMemo(() => expandedSections.flatMap(s => s.rows), [expandedSections]);

  // Refs for per-card DOM nodes so we can measure heights per row
  const itemRefs = React.useRef([]);

  // Refs for category boxes to enable auto-scroll on expand
  const categoryRefs = React.useRef({});

  // Apply per-row equal heights by measuring rendered card heights and setting inline heights
  React.useEffect(() => {
    if (!itemRefs.current) return;
    const run = () => {
      // Reset heights
      itemRefs.current.forEach((el) => { if (el) el.style.height = 'auto'; });
      // For each row, compute max height and apply
      flattenedRows.forEach((rowItems, rowIdx) => {
        const refs = rowItems.map((_, idx) => itemRefs.current[rowIdx * columns + idx]).filter(Boolean);
        if (!refs.length) return;
        const heights = refs.map(r => r.getBoundingClientRect().height || 0);
        const maxH = Math.max(...heights);
        refs.forEach(r => { if (r) r.style.height = `${maxH}px`; });
      });
    };
    // run in rAF to wait for layout (fonts/images)
    let raf = 0;
    const schedule = () => { raf = window.requestAnimationFrame(run); };
    schedule();
    // also run after a short delay (in case fonts loaded late)
    const tId = setTimeout(run, 120);
    // also recalc on full window load
    const onLoad = () => run();
    window.addEventListener('load', onLoad);
    return () => { window.cancelAnimationFrame(raf); clearTimeout(tId); window.removeEventListener('load', onLoad); };
    // Recalc when the flattened rows or columns change
  }, [flattenedRows, columns]);

  // Helper to get translation key for menu item
  const getItemKey = (categoryId, itemName) => {
    // Convert item name to a safe key (e.g. 'Turkish Coffee' -> 'turkish_coffee')
    return `${categoryId}.${itemName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase()}`;
  };

  // Fetch categories with drinks on mount
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingCategories(true);
      try {
        const cats = await fetchCategoriesWithDrinks();
        if (!mounted) return;
        // keep API shape consistent
        let categoriesData = [];
        if (Array.isArray(cats)) {
          categoriesData = cats;
          setApiCategories(cats);
          // set first active category as active if none selected and expand it (single-open accordion)
          const activeCategories = cats.filter(c => c.is_active !== false);
          if (activeCategories.length && !activeCategory) {
            setActiveCategory(activeCategories[0].id);
            setExpandedCategories([activeCategories[0].id]);
          }
        } else if (cats && Array.isArray(cats.categories)) {
          categoriesData = cats.categories;
          setApiCategories(cats.categories);
          const activeCategories = cats.categories.filter(c => c.is_active !== false);
          if (activeCategories.length && !activeCategory) {
            setActiveCategory(activeCategories[0].id);
            setExpandedCategories([activeCategories[0].id]);
          }
        }

        // Extract promotional and featured drinks from all categories
        const { promoItems, featuredItems } = extractMenuHighlights(categoriesData, language);
        setPromotionalDrinks(promoItems);
        setFeaturedDrinks(featuredItems);
      } catch (err) {
        console.error('Failed to load categories with drinks', err);
        setApiError(err.message || String(err));
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update promotional and featured drinks when language changes
  React.useEffect(() => {
    if (!apiCategories || !Array.isArray(apiCategories)) return;

    const { promoItems, featuredItems } = extractMenuHighlights(apiCategories, language);
    setPromotionalDrinks(promoItems);
    setFeaturedDrinks(featuredItems);
  }, [language, apiCategories]);

  // Scroll to top button visibility
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Build a magazine-style 3-column layout using API categories only
  const displayCategories = React.useMemo(() => {
    if (apiCategories && Array.isArray(apiCategories)) {
      // `categories` is already a friendly [{id, label}] mapping (see above)
      return categories;
    }
    // Return empty array if API hasn't loaded yet
    return [];
  }, [apiCategories, categories]);

  // distribute displayCategories into columns; on mobile (columns===1) keep original order so stacking is natural
  const columnsData = React.useMemo(() => {
    if (columns === 1) return [displayCategories];

    // Round-robin distribution for balanced columns
    const cols = Array.from({ length: columns }, () => []);
    displayCategories.forEach((cat, i) => {
      cols[i % columns].push(cat);
    });
    return cols;
  }, [displayCategories, columns]);

  const toggleCategory = (catId) => {
    const isCurrentlyExpanded = expandedCategories.some(id => String(id) === String(catId));

    if (!isCurrentlyExpanded) {
      recordMenuClick('category', catId);
      // Category is being opened - scroll to it after a delay
      setTimeout(() => {
        const categoryElement = categoryRefs.current[catId];
        if (categoryElement) {
          const isMobile = window.innerWidth <= 768;
          const offset = isMobile ? 80 : 100; // Offset for navbar height
          const elementPosition = categoryElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150); // Delay to allow expansion animation
    }

    setExpandedCategories((prev) => {
      const exists = prev.some(id => String(id) === String(catId));
      if (exists) {
        // If clicking the already-open category, collapse it
        return prev.filter(id => String(id) !== String(catId));
      }
      // Single-open accordion: opening a category closes any previously open one
      return [catId];
    });
  };

  const isCategoryExpanded = (catId) => expandedCategories.some(id => String(id) === String(catId));

  const handlePromotionalItemClick = (item) => {
    if (!item.category_id) return;
    recordMenuClick('drink', item.drink_id);

    // Clear any existing highlight timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    // Reset highlight to force animation restart
    setHighlightedDrinkId(null);

    // Set new highlighted item after a small delay to trigger animation
    setTimeout(() => {
      setHighlightedDrinkId(item.drink_id);
    }, 50);

    // Remove highlight after 3 seconds
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedDrinkId(null);
      highlightTimeoutRef.current = null;
    }, 3000);

    // Set active category and expand it
    setActiveCategory(item.category_id);
    setExpandedCategories([item.category_id]);

    // Scroll to the specific drink item after allowing time for rendering
    setTimeout(() => {
      // Try to find the drink item in the DOM
      const drinks = getDrinksForCategory(item.category_id);
      const drinkIndex = drinks.findIndex(d => d.id === item.drink_id);

      if (drinkIndex !== -1) {
        // Find all drink items in this category
        const categoryElement = categoryRefs.current[item.category_id];
        if (categoryElement) {
          const drinkItems = categoryElement.querySelectorAll('[data-drink-item]');
          const targetDrinkElement = drinkItems[drinkIndex];

          if (targetDrinkElement) {
            const isMobile = window.innerWidth <= 768;
            const offset = isMobile ? 200 : 220; // Increased offset for navbar + banner + category header
            const elementPosition = targetDrinkElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
            return;
          }
        }
      }

      // Fallback: scroll to category if drink element not found
      const categoryElement = categoryRefs.current[item.category_id];
      if (categoryElement) {
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 150 : 170;
        const elementPosition = categoryElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 300); // Increased delay to ensure rendering is complete
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', bgcolor: '#181818', display: 'flex', alignItems: 'flex-start', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, color: '#efe8df', maxWidth: '1400px', px: { xs: 2, md: 0 } }}>
        {/* Promotion Banner */}
        <PromotionBanner />

        {/* Promotional Banner - Sticky below navbar */}
        {showPromotionalBanner && promotionalDrinks.length > 0 && (
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <PromotionalBanner
              items={promotionalDrinks}
              onItemClick={handlePromotionalItemClick}
              highlightedDrinkId={highlightedDrinkId}
            />
          </Box>
        )}

        <FeaturedItemsModal
          items={featuredDrinks}
          enabled={showFeaturedShowcase}
          onItemClick={handlePromotionalItemClick}
          language={language}
        />

        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontFamily: 'Yeseva One, serif',
              mb: 2,
              background: 'linear-gradient(135deg, #fff 0%, #eba604ff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.3rem', md: '2.5rem' },
              letterSpacing: { xs: 1, md: 2 },
              textShadow: '0 4px 20px rgba(237, 80, 52, 0.3)'
            }}
          >
            {t('menu.title')}
          </Typography>
          <Box sx={{
            width: { xs: 80, md: 120 },
            height: { xs: 5, md: 6 },
            background: 'linear-gradient(90deg, transparent, #8e6504ff, transparent)',
            borderRadius: 3,
            mx: 'auto',
            boxShadow: '0 0 20px rgba(237, 80, 52, 0.5)'
          }} />
        </Box>

        {/* Language Switcher - Floating */}
        <LanguageSwitcher language={language} setLanguage={handleLanguageChange} style="dark" floating={true} />

        {/* Lounge Policy Banner */}
        <LoungePolicyBanner />

        {/* Columns */}
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {columnsData.map((col, colIdx) => (
            <Grid item xs={12} md={4} key={colIdx}>
              {col.map((cat) => (
                <Box
                  key={cat.id}
                  ref={(el) => { categoryRefs.current[cat.id] = el; }}
                  sx={{
                    mb: 5,
                    p: { xs: 1.5, md: 2.25 },
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    borderRadius: 2,
                    borderLeft: '4px solid rgba(184,124,58,0.18)',
                    borderTop: '1px solid rgba(255,255,255,0.02)',
                    transition: 'background 160ms ease, transform 200ms ease',
                  }}
                >
                  <Box
                    onClick={() => toggleCategory(cat.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(cat.id); } }}
                    role="button"
                    tabIndex={0}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: '#f4e7d9', letterSpacing: 1.2, fontFamily: FONT_FAMILY, fontSize: '1.05rem', display: 'inline-block', px: 1.5, py: 0.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, fontWeight: 700 }}>{cat.label || cat.id}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'inline-block', bgcolor: '#b87c3a', color: '#0b0b0b', fontWeight: 700, fontSize: '0.88rem', px: 1, py: '2px', borderRadius: 16 }}>{getDrinksForCategory(cat.id).length}</Box>
                      <ExpandMoreIcon sx={{ color: '#d9b77a', transform: isCategoryExpanded(cat.id) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms' }} />
                    </Box>
                  </Box>
                  <Box>
                    {isCategoryExpanded(cat.id) && getDrinksForCategory(cat.id).map((item, idx) => {
                      const eurStr = priceEurFor(item);
                      const isHighlighted = highlightedDrinkId && item.id === highlightedDrinkId;
                      return (
                        <Box
                          key={`${cat.id}-${idx}`}
                          data-drink-item
                          component="div"
                          className={tappedDrinkId === item.id ? 'menu-drink menu-drink--tapped' : 'menu-drink'}
                          onClick={() => markDrinkTapped(item.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            py: { xs: 1.5, md: 1.75 },
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            transition: 'transform 200ms cubic-bezier(.2,.9,.2,1), box-shadow 200ms, background 160ms',
                            cursor: 'pointer',
                            userSelect: 'none',
                            WebkitTapHighlightColor: 'transparent',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            outline: 'none',
                            '&:active, &:focus, &:focus-visible, &:focus-within': {
                              outline: 'none !important',
                            },
                            '*': {
                              userSelect: 'none',
                              WebkitTapHighlightColor: 'transparent'
                            },
                            ...(isHighlighted && {
                              background: 'linear-gradient(90deg, rgba(255, 107, 0, 0.2) 0%, rgba(255, 107, 0, 0.1) 100%)',
                              borderLeft: '4px solid #ff6b00',
                              paddingLeft: '12px',
                              animation: 'highlightGlow 1.5s ease-in-out 3',
                              '@keyframes highlightGlow': {
                                '0%, 100%': {
                                  boxShadow: '0 0 10px rgba(255, 107, 0, 0.3)',
                                  transform: 'translateX(0)'
                                },
                                '50%': {
                                  boxShadow: '0 0 20px rgba(255, 107, 0, 0.6)',
                                  transform: 'translateX(4px)'
                                }
                              }
                            })
                          }}
                        >
                          <Box sx={{ pr: 2, flex: 1 }}>
                            <Typography className="menu-drink__name" sx={{ fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>
                              {language === 'bg' && item.name_bg ? item.name_bg : item.name}
                            </Typography>
                            {(language === 'bg' ? (item.description_bg || item.description) : item.description) && (
                              <Typography sx={{ fontSize: '0.92rem', color: '#e0d7ce', mt: 0.5 }}>
                                {language === 'bg' && item.description_bg ? item.description_bg : item.description}
                              </Typography>
                            )}
                            {item.amount && item.unit && (
                              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                                {`${Math.round(item.amount)} ${t(`menu.units.${item.unit.toLowerCase()}`, item.unit)}`}.
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right', width: 120, flexShrink: 0, ml: 2 }}>
                            {showPromotionalBanner && item.is_promotional && item.promotional_price ? (
                              <>
                                <Box sx={{ display: 'inline-block', bgcolor: '#ff6b00', color: '#fff', fontWeight: 700, fontSize: '0.7rem', px: 1, py: '2px', borderRadius: 1, mb: 0.5 }}>
                                  🔥 {t('menu.promo')}
                                </Box>
                                <Typography sx={{ color: '#999', fontWeight: 500, fontSize: '0.85rem', textDecoration: 'line-through', lineHeight: 1.15 }}>
                                  {parseNumber(item.price) != null ? `${parseNumber(item.price).toFixed(2)} €` : ''}
                                </Typography>
                                <Typography sx={{ color: '#ff6b00', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.15 }}>
                                  {parseNumber(item.promotional_price) != null ? `${parseNumber(item.promotional_price).toFixed(2)} €` : ''}
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ color: '#ffdca8', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.15 }}>{eurStr}</Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Scroll to top button - visible only on mobile */}
      {showScrollTop && (
        <Fab
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 20,
            backgroundColor: '#b87c3a',
            color: '#fff',
            zIndex: 1000,
            display: { xs: 'flex', md: 'none' },
            '&:hover': {
              backgroundColor: '#d9b77a',
            },
            boxShadow: '0 4px 12px rgba(184, 124, 58, 0.4)',
          }}
          size="medium"
          aria-label="scroll to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Box>
  );
};

export default Menu;